import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Package,
  Pencil,
  Plus,
  Receipt,
  ShoppingCart,
} from "lucide-react";
import {
  api,
  type ApiError,
  type Product,
  type ProductHolat,
} from "../api";
import { fmtInt, fmtMoney, fmtQty, toMoneyRaw } from "../lib/format";
import { useBranch } from "../lib/use-branch";
import { useTableSort, type SortDir } from "../lib/use-table-sort";
import { cn } from "../lib/utils";
import { BarcodeScannerButton } from "../components/barcode-scanner";
import { InfoTooltip } from "../components/info-tooltip";
import { MiniKpi } from "../components/mini-kpi";
import { ProductAnalyticsDialog } from "../components/product-analytics-dialog";
import type { MovementDocRef } from "../components/product-movements-tab";
import { Money, Mono, StatusChip } from "../components/ui/ledger";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { EmptyState } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { MoneyInput } from "../components/money-input";
import { AutocompleteInput } from "../components/autocomplete-input";
import { Label } from "../components/ui/label";
import { Select } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { PageFilterPopover } from "../components/page-filter-popover";
import { usePageHeader } from "../lib/use-page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

function HolatBadge({ holat }: { holat: ProductHolat }) {
  const { t } = useTranslation("ombor");
  if (holat === "tugagan") return <StatusChip tone="danger">{t("tovars.holat.tugagan")}</StatusChip>;
  if (holat === "kam") return <StatusChip tone="warn">{t("tovars.holat.kam")}</StatusChip>;
  return <StatusChip tone="ok">{t("tovars.holat.yetarli")}</StatusChip>;
}

/** `kodsiz` — faqat shtrix-kodi yo'q tovarlar (ommaviy biriktirish navbati). */
type Filters = { kategoriya: string; holat: string; q: string; kodsiz: boolean };

const EMPTY_FILTERS: Filters = { kategoriya: "", holat: "", q: "", kodsiz: false };

export type StockState = "all" | "yetarli" | "kam" | "tugagan" | "nomutanosib";

/** Dropdown tartibi product qaroridagi beshta holat bilan aynan bir xil. */
export const STOCK_STATES: StockState[] = [
  "all",
  "yetarli",
  "kam",
  "tugagan",
  "nomutanosib",
];

/**
 * Tovar filiallar bo'yicha nomutanosibmi — bir filialda tugagan/kam, boshqa
 * filialda esa hali bor.
 *
 * Aynan shu holat global ostatkada KO'RINMAYDI (jami yetarli bo'lib turadi),
 * shuning uchun alohida filtr: 300 tovarli katalogda buni ustunga qarab
 * qidirib bo'lmaydi.
 */
export function isImbalanced(p: Product): boolean {
  const levels = p.branch_levels;
  if (!levels || levels.length < 2) return false;
  // IKKALA shart ham kerak: qayerdadir tugagan/kam, VA qayerdadir hali
  // yetarli. Faqat birinchisini tekshirsak, hamma filialda barobar kam
  // bo'lgan tovar ham "nomutanosib" bo'lib chiqardi — u esa oddiygina
  // "Kam qolgan", va o'sha holat filtri uni allaqachon ko'rsatadi.
  return (
    levels.some((l) => l.holat !== "yetarli") &&
    levels.some((l) => l.holat === "yetarli")
  );
}

export function matchesStockState(p: Product, state: StockState): boolean {
  // Backend hisoblagan `holat`ni to'g'ridan-to'g'ri ishlatamiz: yetarli, kam
  // va tugagan chegaralarining dashboardda ikkinchi ta'rifi yo'q.
  if (state === "yetarli" || state === "kam" || state === "tugagan") {
    return p.holat === state;
  }
  if (state === "nomutanosib") return isImbalanced(p);
  return true;
}

/**
 * Marja foizi (tannarxga nisbatan) — tannarx 0 bo'lsa aniqlanmagan.
 *
 * Yorliqdan ALOHIDA funksiya: marja ustuni satr ko'rsatadi, lekin saralash
 * son bo'yicha bo'lishi kerak ("25%" ni matn sifatida saralash "100%" dan
 * keyin qo'yardi), va forma ostidagi jonli izoh ham shu hisobni ishlatadi.
 */
export function marginPct(
  tannarx: string | number,
  sotuvNarx: string | number,
): number | null {
  const tan = Number(tannarx);
  if (!tan) return null;
  return ((Number(sotuvNarx) - tan) / tan) * 100;
}

function marginLabel(p: Product, t: TFunction): string {
  const m = marginPct(p.tannarx, p.sotuv_narx);
  return m == null ? t("tovars.marginEmpty") : `${m.toFixed(0)}%`;
}

/**
 * Filiallar kesimidagi qoldiq — bitta katakdagi chiplar qatori.
 *
 * Nega ustun emas, chiplar: Max tarifda filiallar soni cheklanmagan, ya'ni
 * "har filialga bitta ustun" jadvalni cheksiz kengaytirardi (bu yerda
 * allaqachon 11 ta ustun bor). Chiplar o'ralib ketadi va arxivlangan
 * filialni ham ko'tara oladi.
 *
 * Rang holatdan: tugagan → qizil, kam → sariq, yetarli → yashil. Aynan shu
 * "A da qizil, B da yashil" qarama-qarshiligi bir qarashda ko'rinishi kerak.
 */
function BranchLevelChips({ product }: { product: Product }) {
  const { t } = useTranslation("ombor");
  const levels = product.branch_levels;
  if (!levels || levels.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-col items-start gap-1">
      {levels.map((l) => (
        <StatusChip
          key={l.branch_id}
          tone={
            l.holat === "tugagan" ? "danger" : l.holat === "kam" ? "warn" : "ok"
          }
          // Arxivlangan filialda qolib ketgan zaxira — nuqtasiz chip, ya'ni
          // "bu yerda savdo ketmayapti, lekin tovar qolib ketgan".
          dot={l.is_active}
          className={cn(!l.is_active && "opacity-70")}
        >
          <span className="font-normal">{l.branch_name}</span>
          <span className="tabular-nums">{fmtQty(l.miqdor)}</span>
          {!l.is_active && (
            <span className="font-normal">· {t("tovars.inactive")}</span>
          )}
        </StatusChip>
      ))}
    </div>
  );
}

/** Holat saralanish tartibi — eng "og'riqli" holat birinchi. */
const HOLAT_ORDER: Record<ProductHolat, number> = {
  tugagan: 0,
  kam: 1,
  yetarli: 2,
};

type SortKey =
  | "nomi"
  | "artikul"
  | "kategoriya"
  | "tannarx"
  | "sotuvNarx"
  | "margin"
  | "holat";

/**
 * Saralash kalitlari. Sonlar `Number()` orqali (Decimal satr sifatida keladi),
 * bo'sh matn `null` — bunday qatorlar yo'nalishdan qat'i nazar oxirida qoladi.
 */
const SORT_VALUES: Record<SortKey, (p: Product) => string | number | null> = {
  nomi: (p) => p.nomi,
  artikul: (p) => p.artikul || null,
  kategoriya: (p) => p.kategoriya || null,
  tannarx: (p) => Number(p.tannarx),
  sotuvNarx: (p) => Number(p.sotuv_narx),
  margin: (p) => marginPct(p.tannarx, p.sotuv_narx),
  holat: (p) => HOLAT_ORDER[p.holat],
};

function SortableHead({
  column,
  label,
  sortKey,
  sortDir,
  onSort,
  align = "left",
}: {
  column: SortKey;
  label: string;
  sortKey: SortKey | null;
  sortDir: SortDir;
  onSort: (k: SortKey) => void;
  align?: "left" | "right";
}) {
  const active = sortKey === column;
  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead
      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
      className={cn("p-0", align === "right" && "text-right")}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          "inline-flex w-full items-center gap-1 px-3.5 h-10 transition-colors hover:text-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
          align === "right" && "justify-end",
          active && "text-foreground",
        )}
      >
        <span>{label}</span>
        <Icon className={cn("size-3", !active && "opacity-40")} />
      </button>
    </TableHead>
  );
}

export function TovarsPage({
  onXarid,
  onSotuv,
  onOpenDoc,
}: {
  onXarid: (p: Product) => void;
  onSotuv: (p: Product) => void;
  /** Harakatlar jurnalidagi hujjat havolasi (Ombor sahifasi yo'naltiradi). */
  onOpenDoc: (ref: MovementDocRef) => void;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  // `showSelector` = bir nechta FAOL filial. Yagona filialli ustaxonada butun
  // filial o'lchovi (ustun, so'rov parametri) yo'q — bo'sh emas, YO'Q.
  const { selectedBranchId, showSelector } = useBranch();
  const [items, setItems] = useState<Product[]>([]);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [stockState, setStockState] = useState<StockState>("all");
  const [editing, setEditing] = useState<Product | "new" | null>(null);
  const [analyticsFor, setAnalyticsFor] = useState<Product | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Filial tanlangan bo'lsa — ostatka/holat shu filial kesimida.
        // `faol` FILTRSIZ so'raymiz: nofaol kartochkalar ham katalog va
        // zaxira holati bo'yicha ko'rinishda qoladi.
        // Filiallar kesimi faqat kerak bo'lganda so'raladi: javob tovarlar ×
        // filiallar bo'lib o'sadi, yagona filialda esa mutlaqo foydasiz.
        setItems(
          await api.products({
            branch_id: selectedBranchId ?? undefined,
            include_branch_levels: showSelector,
          }),
        );
        setErr(null);
      } catch (e) {
        setErr((e as { message?: string }).message || t("common:common.error"));
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh, selectedBranchId, showSelector, t]);

  const kategoriyalar = useMemo(
    () => Array.from(new Set(items.map((p) => p.kategoriya).filter(Boolean))) as string[],
    [items],
  );
  // Birliklar — shu ustaxona tovarlaridagi mavjud qiymatlar (autocomplete uchun).
  const birliklar = useMemo(
    () => Array.from(new Set(items.map((p) => p.birlik).filter(Boolean))) as string[],
    [items],
  );

  const filtered = useMemo(() => {
    return items.filter((p) => {
      if (!matchesStockState(p, stockState)) return false;
      if (filters.kategoriya && p.kategoriya !== filters.kategoriya) return false;
      if (filters.holat && p.holat !== filters.holat) return false;
      // Nofaol tovarlar barcode biriktirish navbatiga tushmaydi.
      if (filters.kodsiz && !p.faol) return false;
      if (filters.kodsiz && p.barcode) return false;
      if (filters.q) {
        const q = filters.q.toLowerCase();
        const hay = `${p.nomi} ${p.artikul ?? ""} ${p.barcode ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, filters, stockState]);

  // KPI kartalari KO'RINADIGAN qatorlardan hisoblanadi — holat/qidiruv
  // o'zgarganda raqamlar jadval bilan bir xil qoladi.
  const kpi = useMemo(() => {
    let qty = 0;
    let cost = 0;
    let retail = 0;
    for (const p of filtered) {
      const ostatka = Number(p.joriy_ostatka) || 0;
      qty += ostatka;
      cost += ostatka * (Number(p.tannarx) || 0);
      retail += ostatka * (Number(p.sotuv_narx) || 0);
    }
    return { count: filtered.length, qty, cost, retail };
  }, [filtered]);

  // Shtrix-kod qamrovi — faqat FAOL tovarlar bo'yicha va filtrdan QAT'I NAZAR.
  // Ro'yxat nofaol tovarlarni ham yuklaydi, lekin nofaol
  // tovarni kodlash — o'lik yozuvga ish qilish; ular maxrajda turса, maqsadga
  // hech qachon yetib bo'lmasdi. Bu KPI kartalaridan farqli: ular ko'rinadigan
  // qatorlarni sanaydi, bu esa "qancha ish qoldi" ko'rsatkichi.
  const activeItems = useMemo(() => items.filter((p) => p.faol), [items]);
  const withCode = useMemo(
    () => activeItems.filter((p) => p.barcode).length,
    [activeItems],
  );
  const missing = activeItems.length - withCode;

  /** `kodsiz` barcode filtrini ochib/yopadi. */
  function toggleKodsiz() {
    setFilters((f) => ({ ...f, kodsiz: !f.kodsiz }));
  }

  // Saralash filtrdan KEYIN — ko'rinadigan qatorlar tartiblanadi.
  const { sorted, sortKey, sortDir, toggle } = useTableSort(filtered, SORT_VALUES);

  const filterCount = Object.values(filters).filter(Boolean).length;
  const filterNode = useMemo(
    () => (
      <PageFilterPopover
        count={filterCount}
        onClear={() => {
          setFilters(EMPTY_FILTERS);
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="f-q">{t("tovars.filters.search")}</Label>
          <Input
            id="f-q"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder={t("tovars.filters.searchPlaceholder")}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="f-kat">{t("tovars.filters.kategoriya")}</Label>
          <Select
            id="f-kat"
            value={filters.kategoriya}
            onChange={(e) => setFilters((f) => ({ ...f, kategoriya: e.target.value }))}
          >
            <option value="">{t("tovars.filters.all")}</option>
            {kategoriyalar.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="f-holat">{t("tovars.filters.holat")}</Label>
          <Select
            id="f-holat"
            value={filters.holat}
            onChange={(e) => setFilters((f) => ({ ...f, holat: e.target.value }))}
          >
            <option value="">{t("tovars.filters.all")}</option>
            <option value="yetarli">{t("tovars.holat.yetarli")}</option>
            <option value="kam">{t("tovars.holat.kam")}</option>
            <option value="tugagan">{t("tovars.holat.tugagan")}</option>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="f-kodsiz">{t("tovars.filters.barcode")}</Label>
          <Select
            id="f-kodsiz"
            value={filters.kodsiz ? "kodsiz" : ""}
            onChange={(e) => {
              setFilters((f) => ({ ...f, kodsiz: e.target.value === "kodsiz" }));
            }}
          >
            <option value="">{t("tovars.filters.all")}</option>
            <option value="kodsiz">{t("tovars.filters.barcodeMissing")}</option>
          </Select>
        </div>
      </PageFilterPopover>
    ),
    [filters, kategoriyalar, filterCount, t],
  );

  const actionsNode = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => setEditing("new")}>
          <Plus />
          <span className="hidden sm:inline">{t("tovars.newProduct")}</span>
        </Button>
      </div>
    ),
    [t],
  );

  usePageHeader({ filters: filterNode, actions: actionsNode, filterCount });

  return (
    <div className="min-w-0 max-w-full space-y-4 overflow-x-hidden">
      {err && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 text-sm">
          {err}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2 lg:grid-cols-4 [&>*]:min-w-0">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[92px]" />
          ))
        ) : (
          <>
            <MiniKpi label={t("tovars.kpi.products")} value={fmtInt(kpi.count)} />
            <MiniKpi
              label={t("tovars.kpi.quantity")}
              value={fmtQty(kpi.qty)}
              hint={<InfoTooltip text={t("tovars.kpi.quantityHint")} />}
            />
            <MiniKpi label={t("tovars.kpi.costValue")} value={fmtMoney(kpi.cost)} />
            <MiniKpi label={t("tovars.kpi.retailValue")} value={fmtMoney(kpi.retail)} />
          </>
        )}
      </div>

      <div className="warehouse-stock-filter w-full sm:w-64">
        <Label htmlFor="stock-state" className="sr-only">
          {t("tovars.stockState.label")}
        </Label>
        <Select
          id="stock-state"
          value={stockState}
          onChange={(e) => setStockState(e.target.value as StockState)}
        >
          {STOCK_STATES.map((state) => (
            <option key={state} value={state}>
              {t(`tovars.stockState.${state}`)}
            </option>
          ))}
        </Select>
      </div>

      {/* Shtrix-kod qamrovi — "qancha qoldi" ishorasi (analitika emas) va
          ommaviy biriktirish navbatiga bitta bosishda kirish. Faol tovar yo'q
          bo'lsa umuman ko'rsatilmaydi. Surat/maxraj IKKALASI ham faol tovarlar
          bo'yicha — aralashtirilsa qamrov hech qachon 100% ga yetmasdi. */}
      {!loading && activeItems.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
          <span>
            {t("tovars.barcode.coverage", { withCode, total: activeItems.length })}
          </span>
          {(missing > 0 || filters.kodsiz) && (
            <Button
              variant={filters.kodsiz ? "secondary" : "outline"}
              size="sm"
              className="h-7 text-xs"
              aria-pressed={filters.kodsiz}
              onClick={toggleKodsiz}
            >
              {filters.kodsiz
                ? t("tovars.barcode.showAll")
                : t("tovars.barcode.onlyMissing", { count: missing })}
            </Button>
          )}
        </div>
      )}

      <Card className="min-w-0 max-w-full overflow-hidden">
        <CardContent className="min-w-0 max-w-full p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : sorted.length === 0 ? (
            <EmptyState
              icon={<Package />}
              title={t("tovars.empty.title")}
              description={t("tovars.empty.desc")}
              action={
                <Button onClick={() => setEditing("new")}>
                  <Plus />
                  {t("tovars.newProduct")}
                </Button>
              }
            />
          ) : (
            <Table
              className="min-w-[960px]"
              containerClassName="max-w-full overscroll-x-contain"
            >
              <TableHeader>
                <TableRow>
                  {(
                    [
                      ["nomi", "tovars.table.nomi", "left"],
                      ["artikul", "tovars.table.artikul", "left"],
                      ["kategoriya", "tovars.table.kategoriya", "left"],
                      ["tannarx", "tovars.table.tannarx", "right"],
                      ["sotuvNarx", "tovars.table.sotuvNarx", "right"],
                      ["margin", "tovars.table.margin", "right"],
                      ["holat", "tovars.table.holat", "left"],
                    ] as [SortKey, string, "left" | "right"][]
                  ).map(([column, labelKey, align]) => (
                    <SortableHead
                      key={column}
                      column={column}
                      label={t(labelKey)}
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={toggle}
                      align={align}
                    />
                  ))}
                  {/* Filiallar — saralanmaydi (bitta son emas, chiplar to'plami)
                      va faqat ko'p filialli ustaxonada bor. */}
                  {showSelector && (
                    <TableHead>{t("tovars.table.filiallar")}</TableHead>
                  )}
                  {/* Amallar ustuni saralanmaydi. */}
                  <TableHead className="text-right">{t("tovars.table.amallar")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((p) => (
                  <TableRow
                    key={p.id}
                    // Qatorga bosish — tovar analitikasi oynasi.
                    // Nofaol tovar so'nadi (arxivlangan filial qatorlari kabi).
                    className={cn("cursor-pointer", !p.faol && "opacity-60")}
                    role="button"
                    tabIndex={0}
                    aria-label={t("tovars.analytics.open", { nomi: p.nomi })}
                    onClick={() => setAnalyticsFor(p)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setAnalyticsFor(p);
                      }
                    }}
                  >
                    <TableCell className="font-medium">
                      <span className="inline-flex items-center gap-2">
                        {p.nomi}
                        {!p.faol && (
                          <StatusChip tone="muted" dot={false}>
                            {t("tovars.inactive")}
                          </StatusChip>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground font-mono text-xs">
                      {p.artikul || "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {p.kategoriya || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Money>{fmtMoney(p.tannarx)}</Money>
                    </TableCell>
                    <TableCell className="text-right">
                      <Money>{fmtMoney(p.sotuv_narx)}</Money>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground text-xs">
                      <Mono>{marginLabel(p, t)}</Mono>
                    </TableCell>
                    <TableCell>
                      <HolatBadge holat={p.holat} />
                    </TableCell>
                    {showSelector && (
                      <TableCell>
                        <BranchLevelChips product={p} />
                      </TableCell>
                    )}
                    {/* Amallar — qator bosilishini to'sadi, aks holda har bir
                        tugma analitika oynasini ham ochib yuborardi. Klaviatura
                        ham to'sildi: tugmada Enter bosilsa hodisa qatorning
                        onKeyDown'iga chiqib, ikki oyna birga ochilardi. */}
                    <TableCell
                      className="text-right whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <Button variant="ghost" size="sm" onClick={() => onXarid(p)}>
                        <ShoppingCart />
                        {t("tovars.actions.xarid")}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => onSotuv(p)}>
                        <Receipt />
                        {t("tovars.actions.sotuv")}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditing(p)}>
                        <Pencil />
                        {t("tovars.actions.tahrir")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && (
          <ProductForm
            item={editing === "new" ? null : editing}
            kategoriyalar={kategoriyalar}
            birliklar={birliklar}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              setRefresh((x) => x + 1);
            }}
          />
        )}
      </Dialog>

      <Dialog
        open={!!analyticsFor}
        onOpenChange={(o) => !o && setAnalyticsFor(null)}
        contentClassName="max-w-3xl"
      >
        {analyticsFor && (
          <ProductAnalyticsDialog
            product={analyticsFor}
            onOpenDoc={(ref) => {
              // Hujjatga o'tishdan oldin oynani yopamiz — aks holda u
              // maqsad sahifa ustida osilib qolardi.
              setAnalyticsFor(null);
              onOpenDoc(ref);
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

// ============================ Product create/edit ============================

/**
 * Sotuv narxi ostidagi jonli marja izohi — foiz + so'mdagi ustama.
 *
 * Tannarx kiritilmagan bo'lsa marja aniqlanmagan; sotuv narxi tannarxdan past
 * bo'lsa ogohlantiruvchi rangda (zararga sotish odatda xato kiritish).
 */
function MarginHint({
  tannarx,
  sotuvNarx,
}: {
  tannarx: string;
  sotuvNarx: string;
}) {
  const { t } = useTranslation("ombor");
  const pct = marginPct(tannarx, sotuvNarx);
  if (pct == null) {
    return (
      <p className="text-xs text-muted-foreground">
        {t("tovars.form.marginHintNoCost")}
      </p>
    );
  }
  const diff = Number(sotuvNarx || 0) - Number(tannarx);
  const negative = pct < 0;
  return (
    <p className={negative ? "text-xs text-destructive" : "text-xs text-muted-foreground"}>
      {t(negative ? "tovars.form.marginHintNegative" : "tovars.form.marginHint", {
        pct: pct.toFixed(0),
        amount: fmtMoney(diff),
      })}
    </p>
  );
}

function ProductForm({
  item,
  kategoriyalar,
  birliklar,
  onCancel,
  onSaved,
}: {
  item: Product | null;
  kategoriyalar: string[];
  birliklar: string[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  const [nomi, setNomi] = useState(item?.nomi ?? "");
  const [birlik, setBirlik] = useState(item?.birlik ?? "dona");
  const [tannarx, setTannarx] = useState(toMoneyRaw(item?.tannarx));
  const [sotuvNarx, setSotuvNarx] = useState(toMoneyRaw(item?.sotuv_narx));
  const [minOstatka, setMinOstatka] = useState(item?.min_ostatka ?? "");
  const [kategoriya, setKategoriya] = useState(item?.kategoriya ?? "");
  const [artikul, setArtikul] = useState(item?.artikul ?? "");
  const [barcode, setBarcode] = useState(item?.barcode ?? "");
  const [faol, setFaol] = useState(item?.faol ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Shtrix-kod nizosi UMUMIY banner emas, aynan maydon ostida ko'rsatiladi:
  // egasi kodni qaysi tovar egallaganini bilmasa, nima qilishini ham bilmaydi.
  const [barcodeErr, setBarcodeErr] = useState<ApiError | null>(null);

  async function handleScan(code: string) {
    // Qidiruv ham AYNI normallashtirilgan kod bilan ketadi — aks holda maydonda
    // bir qiymat, serverga esa boshqasi borardi.
    const normalized = code.trim().toUpperCase();
    setBarcode(normalized);
    setBarcodeErr(null);
    if (item) return; // tahrirda avtomatik to'ldirmaymiz
    try {
      const found = await api.productByBarcode(normalized);
      setNomi(found.nomi);
      setBirlik(found.birlik);
      setTannarx(toMoneyRaw(found.tannarx));
      setSotuvNarx(toMoneyRaw(found.sotuv_narx));
      toast.info(t("tovars.form.foundFilled"));
    } catch {
      toast.info(t("tovars.form.newBarcode"));
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setBarcodeErr(null);
    const data = {
      nomi,
      birlik,
      tannarx: tannarx || "0",
      sotuv_narx: sotuvNarx || "0",
      min_ostatka: minOstatka || "0",
      kategoriya: kategoriya || null,
      artikul: artikul || null,
      // Skanerdan kelgan chetki bo'shliqlar mijozda ham kesiladi (server ham
      // kesadi); bo'sh qiymat kodni tozalaydi.
      barcode: barcode.trim() || null,
      // Yangi tovar doim faol yaratiladi — bayroq faqat tahrirda yuboriladi.
      ...(item ? { faol } : {}),
    };
    try {
      if (item) await api.updateProduct(item.id, data);
      else await api.createProduct(data);
      toast.success(item ? t("tovars.form.updated") : t("tovars.form.created"));
      onSaved();
    } catch (e) {
      const ae = e as ApiError;
      if (ae.code === "product_barcode_exists") setBarcodeErr(ae);
      else setErr(ae.message || t("common:common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>{item ? t("tovars.form.editTitle") : t("tovars.form.newTitle")}</DialogTitle>
        <DialogDescription>
          {t("tovars.form.desc")}
        </DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="p-nomi">{t("tovars.form.nomi")}</Label>
          <Input id="p-nomi" value={nomi} onChange={(e) => setNomi(e.target.value)} required autoFocus disabled={busy} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="p-birlik">{t("tovars.form.birlik")}</Label>
            <AutocompleteInput id="p-birlik" value={birlik} onChange={setBirlik} suggestions={birliklar} placeholder={t("tovars.form.birlikPlaceholder")} required disabled={busy} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-kat">{t("tovars.form.kategoriya")}</Label>
            <AutocompleteInput id="p-kat" value={kategoriya} onChange={setKategoriya} suggestions={kategoriyalar} disabled={busy} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="p-tan">{t("tovars.form.tannarx")}</Label>
            <MoneyInput id="p-tan" value={tannarx} onChange={setTannarx} disabled={busy} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-sot">{t("tovars.form.sotuvNarx")}</Label>
            <MoneyInput id="p-sot" value={sotuvNarx} onChange={setSotuvNarx} disabled={busy} />
            <MarginHint tannarx={tannarx} sotuvNarx={sotuvNarx} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="p-min" className="flex items-center gap-1">
              {t("tovars.form.minOstatka")}
              <InfoTooltip text={t("tovars.form.minOstatkaHelp")} />
            </Label>
            <Input id="p-min" type="number" min="0" step="0.001" value={minOstatka} onChange={(e) => setMinOstatka(e.target.value)} disabled={busy} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-art">{t("tovars.form.artikul")}</Label>
            <Input id="p-art" value={artikul} onChange={(e) => setArtikul(e.target.value)} disabled={busy} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="p-bar">{t("tovars.form.barcode")}</Label>
          <div className="flex gap-2">
            <Input
              id="p-bar"
              value={barcode}
              // Qo'lda yozilgan ichki kodlar registri birxillashtiriladi.
              onChange={(e) => {
                setBarcode(e.target.value.toUpperCase());
                setBarcodeErr(null);
              }}
              // Stolga ulangan skaner kodni yozib, oxirida Enter bosadi. Buni
              // to'smasak forma qolgan maydonlar to'lmasdan yuborilib ketardi.
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
              aria-invalid={barcodeErr ? true : undefined}
              aria-describedby={barcodeErr ? "p-bar-err" : undefined}
              className={cn(barcodeErr && "border-destructive focus-visible:ring-destructive")}
              disabled={busy}
            />
            <BarcodeScannerButton onScan={handleScan} disabled={busy} />
          </div>
          {/* Formada joy yetarli — server xabari to'liq ko'rsatiladi: u qaysi
              tovar kodni egallaganini va o'chirilgan bo'lsa buni ham aytadi. */}
          {barcodeErr && (
            <p id="p-bar-err" className="text-xs text-destructive">{barcodeErr.message}</p>
          )}
        </div>
        {/* Faollik faqat tahrirda: nofaol tovar POS/kirim tanlovlarida
            ko'rinmaydi, lekin tarixi va ostatkasi saqlanib qoladi. */}
        {item && (
          <label className="flex items-start gap-2.5 rounded-md border p-3">
            <input
              type="checkbox"
              className="mt-0.5 size-4 accent-primary"
              checked={faol}
              onChange={(e) => setFaol(e.target.checked)}
              disabled={busy}
            />
            <span className="space-y-0.5">
              <span className="block text-sm font-medium">{t("tovars.form.faol")}</span>
              <span className="block text-xs text-muted-foreground">
                {t("tovars.form.faolHelp")}
              </span>
            </span>
          </label>
        )}
        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2 text-sm">{err}</div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>{t("common:actions.cancel")}</Button>
        <Button type="submit" disabled={busy}>{busy ? t("common:actions.saving") : t("common:actions.save")}</Button>
      </DialogFooter>
    </form>
  );
}
